import { useMemo, useState } from 'react';

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const MONTH_OPTIONS = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
] as const;

const MMRI_SCORE_TABS = [
  { label: 'Overview', value: 'overview' },
  { label: 'Board & Governance', value: 'board-governance' },
  { label: 'Accounting & Reporting', value: 'accounting-reporting' },
  { label: 'Management Competencies', value: 'management-competencies' },
  { label: 'Data & KPIs', value: 'data-kpis' },
  { label: 'Strategic Roadmap', value: 'strategic-roadmap' },
  { label: 'Technology & Cybersecurity', value: 'technology-cybersecurity' },
  { label: 'Loyalty, Team Culture & ESG', value: 'loyalty-team-culture-esg' }
] as const;

const BOARD_QUALITY_OPTIONS = [
  'No Board',
  'Board of Directors is not appropriately populated. Strategy is not effective as demonstrated by operating performance. Infrequent board interactions.',
  'Board of Directors is somewhat relevant, however strategy is not well articulated or aligned with management execution. Infrequent in-person interaction.',
  'Board of Directors is relevant, and occasionally generates useful business introductions for the management team. Occasionally meets in-person, but usually virtual.',
  'Board of Directors is highly relevant and present, and consistently generating customer, business and strategic introductions for the management team.'
] as const;

const NUMBER_INDEPENDENT_DIRECTORS_OPTIONS = [
  '0',
  'Process started for first independent board member',
  '1',
  'Process started for second independent board member',
  '2'
] as const;

const POSITIONING_FOR_EXIT_OPTIONS = [
  'Exit strategy not discussed at the board level.',
  'Exit strategy discussed infrequently at the board level.',
  'Board has contemplated who the relevant brokers and investment bankers would be to represent the company in a sale or recapitalization.',
  'Occasional conversations with investment bankers, financial and strategic buyers to maximize exit optionality and value. Business strategy reflects understanding of what potential buyers would value.',
  'Early and frequent conversations with investment bankers, financial and strategic buyers to maximize exit optionality and value. Clearly articulated dimensions of what buyers value informs business strategy and resource allocation.'
] as const;

const NUMBER_BOARD_COMMITTEES_OPTIONS = [
  '0',
  'In process of establishing first board committee',
  '1',
  '2',
  '3+'
] as const;

const LEAD_DIRECTOR_OPTIONS = [
  'No',
  'In process of identifying potential Lead Director / Board Chair',
  'Lead director / Board Chair established, however may lack prior CEO / private equity-backed experience',
  'Lead director / Board Chair with prior CEO or private equity-backed executive experience',
  'Highly capable lead director / Board Chair with prior successful CEO and private equity-backed experience'
] as const;

const ANNUAL_PERFORMANCE_REVIEW_OPTIONS = [
  'No',
  'Board evaluates CEO',
  'Board evaluates select C-suite executives',
  '360 reviews of C-level executives and CEO',
  'Well-documented 360 reviews of all C-level executives, including third-party assessments'
] as const;

const RELATIONSHIP_WITH_SMC_OPTIONS = [
  'Does not value communication with Star Mountain',
  'Limited transparency to Star Mountain; does not seek advice or resources from Star Mountain',
  'Informs Star Mountain of issues reactively; willing to receive advice from Star Mountain',
  'Informs Star Mountain of issues proactively and solicits advice to respond to issues',
  'Anticipates potential issues / risks and solicits preventative advice from Star Mountain'
] as const;

const COMPLIANCE_ADHERENCE_OPTIONS = ['Limited', 'Partial', 'Most', 'All'] as const;

const BUDGET_ACHIEVEMENT_OPTIONS = [
  '20%+ miss relative to EBITDA budget',
  '10%-20% miss relative to EBITDA budget',
  'Modest (0-10%) shortfall relative to EBITDA budget',
  'EBITDA budget achieved',
  '15%+ favorable variance to quarterly / annual EBITDA targets'
] as const;

const STRATEGIC_PLAN_ADHERENCE_OPTIONS = [
  'No expressed intention to develop a strategic plan',
  'Strategic plan in development',
  'Strategic plan developing in collaboration with Star Mountain',
  'Board-approved strategic plan reviewed annually',
  'Board-approved strategic plan reviewed annually with third-party input'
] as const;

const CEO_PRESENCE_OPTIONS = [
  'No efforts for brand visibility or thought leadership',
  'Attend industry events',
  'Attend industry events and publish market insights, blogs and webinars',
  'Sponsor and speak at industry conferences and production of thought leadership content',
  'Thought leadership production, conference speaker roles and leadership in industry trade groups demonstrate distinguished mid-market visibility'
] as const;

const C_SUITE_COMPETENCY_OPTIONS = [
  'Role Not Filled',
  'Underperforming',
  'Average Performance',
  'Strong Performer',
  'Best in Class'
] as const;

const EMPLOYEES_EQUITY_OWNERSHIP_OPTIONS = [
  '0%',
  '1-10%',
  '10-19%',
  '20-29%',
  '30%+'
] as const;

const COMMUNICATION_WITH_EMPLOYEES_OPTIONS = [
  'No intention of developing employee alignment through shared cultural values',
  'Mission Statement and / or Shared Values in development',
  'Published Mission Statement & Shared Values',
  'Published Mission Statement and employee communications drive stable employee engagement metrics',
  'Mission Statement, Shared Values, quarterly Town Halls and company newsletters demonstrate improving employee engagement metrics'
] as const;

const CUSTOMER_SATISFACTION_OPTIONS = [
  'No intention of developing a customer feedback mechanism',
  'Developing a process to collect customer feedback',
  'Manual efforts to collect customer feedback',
  'Customer satisfaction / NPS scores are measured with technology on a scheduled basis; data trends demonstrate stability in customer satisfaction / employee engagement',
  'Customer satisfaction and NPS scores are measured frequently and enabled by technology, contextualized with third-party market data; Data trends demonstrate improvement in customer satisfaction / employee engagement and exceed expectations'
] as const;

const DATA_KPIS_OPTIONS = ['No', 'Limited / In Process', 'Yes'] as const;

const LOYALTY_OPTIONS = ['No', 'In Process', 'Yes'] as const;

const TECHNOLOGY_OPTIONS = ['No', 'In Process', 'Implemented', 'Optimized'] as const;

const DIVERSE_EMPLOYEE_BASE_OPTIONS = ['0%', '10%', '15%', '20%', '25%+'] as const;

export interface NewMmriScoreDialogFormValues {
  accountabilityForKpis: null | string;
  annualPerformanceReview: null | string;
  boardBooksDist48HrsAdvance: boolean | null;
  boardQuality: null | string;
  branding: boolean | null;
  budgetAchievement: null | string;
  ceoCompetencyScore: null | string;
  ceoPresence: null | string;
  cfoCompetencyScore: null | string;
  cloudBasedSaas: null | string;
  cmoCooCompetencyScore: null | string;
  communicationWithEmployees: null | string;
  complianceAdherence: null | string;
  croCompetencyScore: null | string;
  ctoCompetencyScore: null | string;
  customerSatisfaction: null | string;
  dataAndDashboardForDecisions: null | string;
  disasterPlan: null | string;
  diverseEmployeeBase: null | string;
  fourToSixBoardMeetingsAnnually: boolean | null;
  implementationPerformancePlan: boolean | null;
  kpiReporting: null | string;
  leadDirector: null | string;
  midLevelEmployeeRetention: null | number;
  modernInfra: null | string;
  month: null | number;
  noDaysAfterEomCloseBooks: null | number;
  noDaysReceiveAudit: null | number;
  noEmployeesEquityOwnership: null | string;
  numberBoardCommittees: null | string;
  numberIndependentDirectors: null | string;
  objectivesAligned: boolean | null;
  positioningForExit: null | string;
  relationshipWithSmc: null | string;
  smcOperatingNetwork: null | string;
  socCompliantProtocol: null | string;
  strategicPlanAdherence: null | string;
  talentDevelopment: null | string;
  usingLedgerAndErpProvider: null | string;
  year: null | number;
}

const DEFAULT_VALUES: NewMmriScoreDialogFormValues = {
  accountabilityForKpis: null,
  annualPerformanceReview: null,
  boardBooksDist48HrsAdvance: null,
  boardQuality: null,
  branding: null,
  budgetAchievement: null,
  ceoCompetencyScore: null,
  ceoPresence: null,
  cfoCompetencyScore: null,
  cloudBasedSaas: null,
  cmoCooCompetencyScore: null,
  communicationWithEmployees: null,
  complianceAdherence: null,
  croCompetencyScore: null,
  ctoCompetencyScore: null,
  customerSatisfaction: null,
  dataAndDashboardForDecisions: null,
  disasterPlan: null,
  diverseEmployeeBase: null,
  fourToSixBoardMeetingsAnnually: null,
  implementationPerformancePlan: null,
  kpiReporting: null,
  leadDirector: null,
  midLevelEmployeeRetention: null,
  modernInfra: null,
  month: null,
  noDaysAfterEomCloseBooks: null,
  noDaysReceiveAudit: null,
  noEmployeesEquityOwnership: null,
  numberBoardCommittees: null,
  numberIndependentDirectors: null,
  objectivesAligned: null,
  positioningForExit: null,
  relationshipWithSmc: null,
  smcOperatingNetwork: null,
  socCompliantProtocol: null,
  strategicPlanAdherence: null,
  talentDevelopment: null,
  usingLedgerAndErpProvider: null,
  year: null
};

export function NewMmriScoreDialog({
  allowedTabs,
  defaultTab = 'overview',
  initialValues,
  isSaving,
  onOpenChange,
  onSave,
  open,
  title = 'New MMRI Score'
}: {
  allowedTabs?: string[];
  defaultTab?: string;
  initialValues?: Partial<NewMmriScoreDialogFormValues>;
  isSaving?: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (values: NewMmriScoreDialogFormValues) => void;
  open: boolean;
  title?: string;
}) {
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 2020 + 1 }, (_, index) => {
      return 2020 + index;
    });
  }, []);

  const visibleTabs = useMemo(() => {
    if (!allowedTabs?.length) return MMRI_SCORE_TABS;
    return MMRI_SCORE_TABS.filter(tab => allowedTabs.includes(tab.value));
  }, [allowedTabs]);

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [values, setValues] = useState<NewMmriScoreDialogFormValues>({
    ...DEFAULT_VALUES,
    ...initialValues
  });

  const requiresOverview = !allowedTabs || allowedTabs.includes('overview');
  const isOverviewComplete = values.month != null && values.year != null;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setValues(DEFAULT_VALUES);
    }
    onOpenChange(nextOpen);
  };

  const updateValues = (next: Partial<NewMmriScoreDialogFormValues>) => {
    setValues(prev => ({ ...prev, ...next }));
  };

  return (
    <Dialog
      onOpenChange={handleOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-[1500px]"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="h-[calc(100dvh-178px)]">
          <Tabs
            onValueChange={setActiveTab}
            value={activeTab}
          >
            <TabsList
              className={
                title.startsWith('Edit')
                  ? 'hidden'
                  : 'h-auto! w-full justify-start overflow-x-auto overflow-y-hidden'
              }
            >
              {
              !title.startsWith('Edit') && visibleTabs.map(tab => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {visibleTabs.some(tab => tab.value === 'overview') ? (
              <TabsContent value="overview">
                <div className="border-border rounded-md border p-4">
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>Month</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ month: Number(value) || null });
                        }}
                        value={values.month == null ? '' : String(values.month)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select month" />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTH_OPTIONS.map(option => (
                            <SelectItem
                              key={option.value}
                              value={String(option.value)}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>Year</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ year: Number(value) || null });
                        }}
                        value={values.year == null ? '' : String(values.year)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select year" />
                        </SelectTrigger>
                        <SelectContent>
                          {yearOptions.map(year => (
                            <SelectItem
                              key={year}
                              value={String(year)}
                            >
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                </div>
              </TabsContent>
            ) : null}
            {visibleTabs.some(tab => tab.value === 'board-governance') ? (
              <TabsContent value="board-governance">
                <div className="space-y-4">
                  <Field>
                    <div>
                        <FieldLabel>
                      Board consists of value-added, independent directors capable
                      of helping the CEO and management team
                    </FieldLabel>
                        <p className="text-muted-foreground text-xs">
                        Understanding of the business and its industry, Effectiveness
                        in aligning management&apos;s short-term goals and tactics,
                        Attendance record of Board members (with at least some being
                        in-person rather than virtual or telephonic)
                        </p>
                    </div>
                    <Select
                      onValueChange={value => {
                        updateValues({ boardQuality: value });
                      }}
                      value={values.boardQuality ?? ''}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select value" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOARD_QUALITY_OPTIONS.map(option => (
                          <SelectItem
                            key={option}
                            value={option}
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>Number of Independent Directors</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ numberIndependentDirectors: value });
                        }}
                        value={values.numberIndependentDirectors ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {NUMBER_INDEPENDENT_DIRECTORS_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>
                        Active and early in positioning the company for a
                        successful exit
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ positioningForExit: value });
                        }}
                        value={values.positioningForExit ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {POSITIONING_FOR_EXIT_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>4-6 Board Meetings Per Year</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({
                            fourToSixBoardMeetingsAnnually:
                              value === 'yes' ? true : value === 'no' ? false : null
                          });
                        }}
                        value={
                          values.fourToSixBoardMeetingsAnnually == null
                            ? ''
                            : values.fourToSixBoardMeetingsAnnually
                              ? 'yes'
                              : 'no'
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>Board Books Distributed 48 Hours in Advance</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({
                            boardBooksDist48HrsAdvance:
                              value === 'yes' ? true : value === 'no' ? false : null
                          });
                        }}
                        value={
                          values.boardBooksDist48HrsAdvance == null
                            ? ''
                            : values.boardBooksDist48HrsAdvance
                              ? 'yes'
                              : 'no'
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>Number of Board Committees</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ numberBoardCommittees: value });
                        }}
                        value={values.numberBoardCommittees ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {NUMBER_BOARD_COMMITTEES_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>Lead Director</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ leadDirector: value });
                        }}
                        value={values.leadDirector ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_DIRECTOR_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field className="self-end">
                      <FieldLabel>Annual Performance Review</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ annualPerformanceReview: value });
                        }}
                        value={values.annualPerformanceReview ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {ANNUAL_PERFORMANCE_REVIEW_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <div>
                        <FieldLabel>
                            Values relationship with Star Mountain in situation where
                            Star Mountain does not control the board
                        </FieldLabel>
                        <p className="text-muted-foreground text-xs">
                            Informs Star Mountain of key management and company issues
                            on a timely basis, Solicits input from Star Mountain on
                            important matters
                        </p>
                      </div>
                      <Select
                        onValueChange={value => {
                          updateValues({ relationshipWithSmc: value });
                        }}
                        value={values.relationshipWithSmc ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {RELATIONSHIP_WITH_SMC_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                </div>
              </TabsContent>
            ) : null}
            {visibleTabs.some(tab => tab.value === 'accounting-reporting') ? (
              <TabsContent value="accounting-reporting">
                <div className="space-y-4">
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>Number of Days to Receive Audit</FieldLabel>
                      <Input
                        min={0}
                        onChange={event => {
                          const value = event.target.value;
                          updateValues({
                            noDaysReceiveAudit: value ? Number(value) : null
                          });
                        }}
                        type="number"
                        value={values.noDaysReceiveAudit ?? ''}
                      />
                    </Field>
                    <Field>
                      <FieldLabel>
                        Number of Days After End of Month to Close the Books
                      </FieldLabel>
                      <Input
                        min={0}
                        onChange={event => {
                          const value = event.target.value;
                          updateValues({
                            noDaysAfterEomCloseBooks: value ? Number(value) : null
                          });
                        }}
                        type="number"
                        value={values.noDaysAfterEomCloseBooks ?? ''}
                      />
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>
                        Implementation of Performance-Driven Bonus Plan
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({
                            implementationPerformancePlan:
                              value === 'yes' ? true : value === 'no' ? false : null
                          });
                        }}
                        value={
                          values.implementationPerformancePlan == null
                            ? ''
                            : values.implementationPerformancePlan
                              ? 'yes'
                              : 'no'
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>
                        Compliance and Regulatory Standards Adherence
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ complianceAdherence: value });
                        }}
                        value={values.complianceAdherence ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMPLIANCE_ADHERENCE_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                </div>
              </TabsContent>
            ) : null}
            {visibleTabs.some(tab => tab.value === 'management-competencies') ? (
              <TabsContent value="management-competencies">
                <div className="space-y-4">
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>
                        Achievement of budgeted annual / quarterly financial
                        results
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ budgetAchievement: value });
                        }}
                        value={values.budgetAchievement ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGET_ACHIEVEMENT_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>
                        Adoption of and adherence to a longer-term (3-5 year),
                        Board-approved strategic plan
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ strategicPlanAdherence: value });
                        }}
                        value={values.strategicPlanAdherence ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {STRATEGIC_PLAN_ADHERENCE_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <Field>
                    <div>
                        <FieldLabel>
                        External, market "presence" of the CEO to develop
                        relationships with customers, prospects and potential
                        acquirers
                        </FieldLabel>
                        <p className="text-muted-foreground text-xs">
                        Conference speaking and panel roles, whitepapers, in-person
                        meetings and networking
                        </p>
                    </div>
                    <Select
                      onValueChange={value => {
                        updateValues({ ceoPresence: value });
                      }}
                      value={values.ceoPresence ?? ''}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select value" />
                      </SelectTrigger>
                      <SelectContent>
                        {CEO_PRESENCE_OPTIONS.map(option => (
                          <SelectItem
                            key={option}
                            value={option}
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <p className="text-sm font-semibold">
                    Competent Management Team across critical C-Suite positions /
                    Key Roles Are Filled:
                  </p>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>CEO</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ ceoCompetencyScore: value });
                        }}
                        value={values.ceoCompetencyScore ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {C_SUITE_COMPETENCY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>CFO</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ cfoCompetencyScore: value });
                        }}
                        value={values.cfoCompetencyScore ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {C_SUITE_COMPETENCY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>CMO/COO</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ cmoCooCompetencyScore: value });
                        }}
                        value={values.cmoCooCompetencyScore ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {C_SUITE_COMPETENCY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>CTO</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ ctoCompetencyScore: value });
                        }}
                        value={values.ctoCompetencyScore ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {C_SUITE_COMPETENCY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>CRO (Head of Sales)</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ croCompetencyScore: value });
                        }}
                        value={values.croCompetencyScore ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {C_SUITE_COMPETENCY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>Midlevel Employee Retention & Tracked</FieldLabel>
                      <Input
                        min={0}
                        onChange={event => {
                          const value = event.target.value;
                          updateValues({
                            midLevelEmployeeRetention: value
                              ? Number(value) / 100
                              : null
                          });
                        }}
                        type="number"
                        value={
                          values.midLevelEmployeeRetention != null
                            ? values.midLevelEmployeeRetention * 100
                            : ''
                        }
                      />
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field className="self-end">
                      <FieldLabel>
                        Number of Employees Incentivized by Equity Ownership
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ noEmployeesEquityOwnership: value });
                        }}
                        value={values.noEmployeesEquityOwnership ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {EMPLOYEES_EQUITY_OWNERSHIP_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <div>
                        <FieldLabel>
                        Frequent and effective communications with employees.
                      </FieldLabel>
                        <p className="text-muted-foreground text-xs">
                        Mission statements, shared values, periodic town halls and
                        / or company newsletters.
                        </p>
                      </div>
                      <Select
                        onValueChange={value => {
                          updateValues({ communicationWithEmployees: value });
                        }}
                        value={values.communicationWithEmployees ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMMUNICATION_WITH_EMPLOYEES_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <Field>
                    <div>
                        <FieldLabel>
                        Customer satisfaction / external feedback scores (e.g.,
                        NPS)
                        </FieldLabel>
                        <p className="text-muted-foreground text-xs">
                        Are they tracked and is the company improving them?
                        </p>
                    </div>
                    <Select
                      onValueChange={value => {
                        updateValues({ customerSatisfaction: value });
                      }}
                      value={values.customerSatisfaction ?? ''}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select value" />
                      </SelectTrigger>
                      <SelectContent>
                        {CUSTOMER_SATISFACTION_OPTIONS.map(option => (
                          <SelectItem
                            key={option}
                            value={option}
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </TabsContent>
            ) : null}
            {visibleTabs.some(tab => tab.value === 'data-kpis') ? (
              <TabsContent value="data-kpis">
                <div className="space-y-4">
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>
                        Utilizes Data and Dashboard to Make Real-Time Business
                        Decisions
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ dataAndDashboardForDecisions: value });
                        }}
                        value={values.dataAndDashboardForDecisions ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {DATA_KPIS_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>
                        KPI Reporting to Support Financial Performance
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ kpiReporting: value });
                        }}
                        value={values.kpiReporting ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {DATA_KPIS_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <Field>
                    <FieldLabel>
                      Clear Management Accountability for KPI Achievement
                    </FieldLabel>
                    <Select
                      onValueChange={value => {
                        updateValues({ accountabilityForKpis: value });
                      }}
                      value={values.accountabilityForKpis ?? ''}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select value" />
                      </SelectTrigger>
                      <SelectContent>
                        {DATA_KPIS_OPTIONS.map(option => (
                          <SelectItem
                            key={option}
                            value={option}
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </TabsContent>
            ) : null}
            {visibleTabs.some(tab => tab.value === 'strategic-roadmap') ? (
              <TabsContent value="strategic-roadmap">
                <Field>
                  <FieldLabel>
                    Objectives Aligned with Market Dynamics & Priorities of
                    Strategic Buyers
                  </FieldLabel>
                  <Select
                    onValueChange={value => {
                      updateValues({
                        objectivesAligned:
                          value === 'yes' ? true : value === 'no' ? false : null
                      });
                    }}
                    value={
                      values.objectivesAligned == null
                        ? ''
                        : values.objectivesAligned
                          ? 'yes'
                          : 'no'
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </TabsContent>
            ) : null}
            {visibleTabs.some(tab => tab.value === 'technology-cybersecurity') ? (
              <TabsContent value="technology-cybersecurity">
                <div className="space-y-4">
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>
                        Using Proven Middle Market General Ledger and ERP Provider
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ usingLedgerAndErpProvider: value });
                        }}
                        value={values.usingLedgerAndErpProvider ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {TECHNOLOGY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>
                        Utilization of Cloud-Based Software Applications (SaaS)
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ cloudBasedSaas: value });
                        }}
                        value={values.cloudBasedSaas ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {TECHNOLOGY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>Modern Technological Infrastructure</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ modernInfra: value });
                        }}
                        value={values.modernInfra ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {TECHNOLOGY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>Upgraded Security Protocols</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ socCompliantProtocol: value });
                        }}
                        value={values.socCompliantProtocol ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {TECHNOLOGY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <Field>
                    <FieldLabel>Fulsome Disaster Recovery Plan in Place</FieldLabel>
                    <Select
                      onValueChange={value => {
                        updateValues({ disasterPlan: value });
                      }}
                      value={values.disasterPlan ?? ''}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select value" />
                      </SelectTrigger>
                      <SelectContent>
                        {TECHNOLOGY_OPTIONS.map(option => (
                          <SelectItem
                            key={option}
                            value={option}
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </TabsContent>
            ) : null}
            {visibleTabs.some(tab => tab.value === 'loyalty-team-culture-esg') ? (
              <TabsContent value="loyalty-team-culture-esg">
                <div className="space-y-4">
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>
                        Utilization of Talent Development Services
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ talentDevelopment: value });
                        }}
                        value={values.talentDevelopment ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {LOYALTY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>
                        Utilization of Star Mountain Operating Network
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ smcOperatingNetwork: value });
                        }}
                        value={values.smcOperatingNetwork ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {LOYALTY_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                  <FieldGroup className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field>
                      <FieldLabel>
                        Branded Goods and Connection to Social Media Outlets to
                        Support Business
                      </FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({
                            branding:
                              value === 'yes' ? true : value === 'no' ? false : null
                          });
                        }}
                        value={
                          values.branding == null
                            ? ''
                            : values.branding
                              ? 'yes'
                              : 'no'
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel>Diverse Employee Base</FieldLabel>
                      <Select
                        onValueChange={value => {
                          updateValues({ diverseEmployeeBase: value });
                        }}
                        value={values.diverseEmployeeBase ?? ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select value" />
                        </SelectTrigger>
                        <SelectContent>
                          {DIVERSE_EMPLOYEE_BASE_OPTIONS.map(option => (
                            <SelectItem
                              key={option}
                              value={option}
                            >
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </FieldGroup>
                </div>
              </TabsContent>
            ) : null}
          </Tabs>
        </DialogBody>
        <Separator />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            disabled={(isSaving ?? false) || (requiresOverview && !isOverviewComplete)}
            onClick={() => {
              onSave?.(values);
            }}
            type="button"
          >
            {isSaving ? <Spinner /> : null}
            Save Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
